// ConfirmDialog — drop-in replacement for the browser's confirm().
//
// Usage:
//   const confirm = useConfirm();
//   async function remove() {
//     const ok = await confirm({
//       title:        "Delete team?",
//       message:      "This soft-deletes the team.",
//       confirmLabel: "Delete",
//       tone:         "danger",   // "danger" | "warning" | "info"
//     });
//     if (!ok) return;
//     // …do the delete…
//   }
//
// Returns a promise that resolves to true (confirmed) or false
// (dismissed / cancelled / Escape / backdrop click). Only one dialog
// can be visible at a time — a second confirm() call while one is
// already open resolves the earlier promise with false and takes
// over. Renders via createPortal to document.body so it always
// clears whatever stacking context it was called from.

import React, {
  createContext, useCallback, useContext, useEffect, useRef, useState
} from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Trash2, Info, X, Copy, Check, KeyRound } from "lucide-react";

const ConfirmCtx = createContext(null);

const DEFAULTS = {
  title:        "Are you sure?",
  message:      "",
  confirmLabel: "Confirm",
  cancelLabel:  "Cancel",
  tone:         "danger",
};

export function ConfirmProvider({ children }) {
  const [opts, setOpts]           = useState(null);   // confirm dialog
  const [promptOpts, setPromptOpts] = useState(null); // prompt dialog
  const [creds, setCreds]         = useState(null);   // credentials/alert
  const resolveRef        = useRef(null);
  const promptResolveRef  = useRef(null);
  const credsResolveRef   = useRef(null);
  const confirmBtnRef     = useRef(null);
  const promptInputRef    = useRef(null);

  const request = useCallback((raw) => {
    // If a previous dialog is still open, resolve it as cancelled
    // before opening the new one — protects against weird UX where
    // two confirms overlap.
    if (resolveRef.current) {
      resolveRef.current(false);
      resolveRef.current = null;
    }
    // Backward-compat: `confirm("Delete?")` behaves like the browser's
    // confirm — the string becomes the dialog message and we pick a
    // reasonable danger-tone title. Full customisation via the object
    // form: confirm({ title, message, confirmLabel, tone }).
    const norm = typeof raw === "string"
      ? { title: DEFAULTS.title, message: raw }
      : (raw || {});
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setOpts({ ...DEFAULTS, ...norm });
    });
  }, []);

  // Focus the confirm button on open so keyboard users can Enter to
  // confirm or Escape to cancel without hunting for the button.
  useEffect(() => {
    if (opts && confirmBtnRef.current) {
      // Next tick so the DOM is painted before we focus.
      const id = window.setTimeout(() => {
        confirmBtnRef.current?.focus();
      }, 30);
      return () => window.clearTimeout(id);
    }
  }, [opts]);

  // Escape cancels, Enter confirms — but only while a dialog is open.
  useEffect(() => {
    if (!opts) return;
    function onKey(e) {
      if (e.key === "Escape") { e.preventDefault(); dismiss(false); }
      // Enter handled naturally by the focused button; nothing to do.
    }
    document.addEventListener("keydown", onKey);
    // Body-scroll lock while the dialog is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line
  }, [opts]);

  function dismiss(answer) {
    if (resolveRef.current) {
      resolveRef.current(!!answer);
      resolveRef.current = null;
    }
    setOpts(null);
  }

  // ── PromptDialog — inline text input replacement for window.prompt.
  //    Returns a promise that resolves to the trimmed string on submit
  //    or null on cancel/Escape/backdrop.
  const requestPrompt = useCallback((raw) => {
    if (promptResolveRef.current) {
      promptResolveRef.current(null);
      promptResolveRef.current = null;
    }
    const norm = typeof raw === "string" ? { title: raw } : (raw || {});
    return new Promise((resolve) => {
      promptResolveRef.current = resolve;
      setPromptOpts({
        title:        "Enter a value",
        message:      "",
        placeholder:  "",
        confirmLabel: "OK",
        cancelLabel:  "Cancel",
        tone:         "info",
        multiline:    false,
        required:     false,
        defaultValue: "",
        ...norm,
      });
    });
  }, []);
  function dismissPrompt(value) {
    if (promptResolveRef.current) {
      promptResolveRef.current(value);
      promptResolveRef.current = null;
    }
    setPromptOpts(null);
  }

  // ── AlertDialog — styled replacement for window.alert(). Ideal for
  //    surfacing sensitive one-time data (temp credentials) with a
  //    copy-to-clipboard control. Resolves when dismissed.
  const requestAlert = useCallback((raw) => {
    if (credsResolveRef.current) {
      credsResolveRef.current(false);
      credsResolveRef.current = null;
    }
    const norm = typeof raw === "string" ? { title: raw } : (raw || {});
    return new Promise((resolve) => {
      credsResolveRef.current = resolve;
      setCreds({
        title:        "Notice",
        message:      "",
        confirmLabel: "Got it",
        tone:         "info",
        copyable:     null,   // { label, value }[] to render as copy-rows
        icon:         null,   // override lucide icon component
        ...norm,
      });
    });
  }, []);
  function dismissAlert() {
    if (credsResolveRef.current) {
      credsResolveRef.current(true);
      credsResolveRef.current = null;
    }
    setCreds(null);
  }

  const value = {
    confirm: request,
    prompt:  requestPrompt,
    alert:   requestAlert,
  };

  return (
    <ConfirmCtx.Provider value={value}>
      {children}
      {opts && createPortal(
        <ConfirmModal
          opts={opts}
          confirmBtnRef={confirmBtnRef}
          onCancel={() => dismiss(false)}
          onConfirm={() => dismiss(true)}
        />,
        document.body
      )}
      {promptOpts && createPortal(
        <PromptModal
          opts={promptOpts}
          inputRef={promptInputRef}
          onCancel={() => dismissPrompt(null)}
          onSubmit={(val) => dismissPrompt(val)}
        />,
        document.body
      )}
      {creds && createPortal(
        <AlertModal
          opts={creds}
          onDismiss={dismissAlert}
        />,
        document.body
      )}
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx.confirm;
}
export function usePrompt() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("usePrompt must be used inside <ConfirmProvider>");
  return ctx.prompt;
}
export function useAlert() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useAlert must be used inside <ConfirmProvider>");
  return ctx.alert;
}

// ── PromptModal — text-input replacement for window.prompt ────
function PromptModal({ opts, inputRef, onCancel, onSubmit }) {
  const [value, setValue] = useState(opts.defaultValue || "");
  const tone = opts.tone || "info";

  // Autofocus the input on open + Escape cancels + Enter submits (only
  // on single-line; multi-line keeps Enter for newlines).
  useEffect(() => {
    const id = window.setTimeout(() => inputRef.current?.focus(), 30);
    function onKey(e) {
      if (e.key === "Escape") { e.preventDefault(); onCancel(); }
    }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [inputRef, onCancel]);

  const canSubmit = opts.required ? value.trim().length > 0 : true;

  function submit(e) {
    e?.preventDefault?.();
    if (!canSubmit) return;
    onSubmit(value.trim());
  }

  return (
    <div className="confirmDialog" role="alertdialog" aria-modal="true"
         aria-labelledby="promptDialog-title" onClick={onCancel}>
      <form
        className={`confirmDialog-body confirmDialog-body--${tone}`}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <button type="button" className="confirmDialog-close" onClick={onCancel} aria-label="Close">
          <X size={16}/>
        </button>

        <div className={`confirmDialog-iconWrap confirmDialog-iconWrap--${tone}`} aria-hidden="true">
          <Info size={22}/>
        </div>

        <h3 id="promptDialog-title" className="confirmDialog-title">{opts.title}</h3>
        {opts.message && <p className="confirmDialog-message">{opts.message}</p>}

        {opts.multiline ? (
          <textarea
            ref={inputRef}
            className="confirmDialog-input confirmDialog-input--multi"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={opts.placeholder}
            rows={4}
          />
        ) : (
          <input
            ref={inputRef}
            type="text"
            className="confirmDialog-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={opts.placeholder}
          />
        )}

        <div className="confirmDialog-actions">
          <button type="button" className="confirmDialog-btn confirmDialog-btn--ghost" onClick={onCancel}>
            {opts.cancelLabel}
          </button>
          <button
            type="submit"
            className={`confirmDialog-btn confirmDialog-btn--${tone}`}
            disabled={!canSubmit}
          >
            {opts.confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── AlertModal — replacement for window.alert() with optional
//    copy-to-clipboard rows (great for credential handoff). ─────
function AlertModal({ opts, onDismiss }) {
  const tone = opts.tone || "info";
  const [copied, setCopied] = useState(null);
  const IconCmp = opts.icon || (tone === "danger" ? AlertTriangle : tone === "warning" ? AlertTriangle : Info);

  async function copy(value, key) {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(key);
      window.setTimeout(() => setCopied(null), 1600);
    } catch {
      // ignore — some browsers block without user gesture, but this
      // handler IS in a click so it should generally work
    }
  }

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onDismiss(); }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onDismiss]);

  return (
    <div className="confirmDialog" role="alertdialog" aria-modal="true"
         aria-labelledby="alertDialog-title" onClick={onDismiss}>
      <div className={`confirmDialog-body confirmDialog-body--${tone}`}
           onClick={(e) => e.stopPropagation()}>
        <button type="button" className="confirmDialog-close" onClick={onDismiss} aria-label="Close">
          <X size={16}/>
        </button>

        <div className={`confirmDialog-iconWrap confirmDialog-iconWrap--${tone}`} aria-hidden="true">
          <IconCmp size={22}/>
        </div>

        <h3 id="alertDialog-title" className="confirmDialog-title">{opts.title}</h3>
        {opts.message && <p className="confirmDialog-message">{opts.message}</p>}

        {Array.isArray(opts.copyable) && opts.copyable.length > 0 && (
          <div className="confirmDialog-copyRows">
            {opts.copyable.map((row) => (
              <div key={row.label} className="confirmDialog-copyRow">
                <div className="confirmDialog-copyRow-body">
                  <span className="confirmDialog-copyRow-label">{row.label}</span>
                  <code className="confirmDialog-copyRow-value">{row.value}</code>
                </div>
                <button
                  type="button"
                  className="confirmDialog-copyRow-btn"
                  onClick={() => copy(row.value, row.label)}
                  aria-label={`Copy ${row.label}`}
                >
                  {copied === row.label
                    ? <><Check size={13}/> Copied</>
                    : <><Copy size={13}/> Copy</>}
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="confirmDialog-actions">
          <button
            type="button"
            className={`confirmDialog-btn confirmDialog-btn--${tone}`}
            onClick={onDismiss}
          >
            {opts.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ConfirmModal ───────────────────────────────────────────────
function ConfirmModal({ opts, confirmBtnRef, onCancel, onConfirm }) {
  const tone = opts.tone || "danger";
  const Icon = tone === "danger"
    ? Trash2
    : tone === "warning"
      ? AlertTriangle
      : Info;

  return (
    <div
      className="confirmDialog"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirmDialog-title"
      aria-describedby={opts.message ? "confirmDialog-message" : undefined}
      onClick={onCancel}
    >
      <div
        className={`confirmDialog-body confirmDialog-body--${tone}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="confirmDialog-close"
          onClick={onCancel}
          aria-label="Close"
        >
          <X size={16}/>
        </button>

        <div className={`confirmDialog-iconWrap confirmDialog-iconWrap--${tone}`} aria-hidden="true">
          <Icon size={22}/>
        </div>

        <h3 id="confirmDialog-title" className="confirmDialog-title">
          {opts.title}
        </h3>

        {opts.message && (
          <p id="confirmDialog-message" className="confirmDialog-message">
            {opts.message}
          </p>
        )}

        <div className="confirmDialog-actions">
          <button
            type="button"
            className="confirmDialog-btn confirmDialog-btn--ghost"
            onClick={onCancel}
          >
            {opts.cancelLabel}
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            className={`confirmDialog-btn confirmDialog-btn--${tone}`}
            onClick={onConfirm}
          >
            {opts.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
